import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'

import Pagination from '../Pagination'
import RowsPerPage from '../RowsPerPage'

import TableHeaders from '../TableHeaders'
import TableRows from '../TableRows'
import TopControls from '../TopControls'

import Loading from '../Loading'

const Table = ({
  topControls,
  tablePayload,
  className = '',
  filterGroupsConfig,
  styleCallback,
  loading = false,
  error = false,
  starColumn = false,
  isHeaderVisible = true,
  UUIDCheckboxes = false
}) => {
  const tableElement = useRef(null)
  const activeColumns = tablePayload.schema.filter((d) => d.isColumn !== false && d.isColumnVisible !== false && d.name !== tablePayload.stickyColumn?.name)

  const [isLeftScrolled, setIsLeftScrolled] = useState(false)
  const [isRightScrolled, setIsRightScrolled] = useState(false)

  const handleScroll = () => {
    const container = tableElement.current
    const maxScrollAmount = container.scrollWidth - container.clientWidth
    if (container.scrollLeft !== 0) {
      setIsLeftScrolled(true)
      setIsRightScrolled(true)
      if (container.scrollLeft > maxScrollAmount - 10) {
        setIsRightScrolled(false)
      }
    }
    if (container.scrollLeft < 1) {
      setIsLeftScrolled(false)
    }
  }

  let gridColumnWidths = `${activeColumns.map((a) => {
    if (a.isOptionColumn) {
      return 'auto'
    }
    return `minmax(${a.startingWidth ? a.startingWidth : '100'}px, ${a.fractionalWidth ? a.fractionalWidth : 1}fr)`
  }).join(' ')}`

  if (isHeaderVisible) {
    const stickyColumnStartingWidth = tablePayload.stickyColumn?.startingWidth || 75

    if (UUIDCheckboxes) {
      if (tablePayload.stickyColumn) {
        // Sticky Column + UUID Checkboxes
        gridColumnWidths = `45px minmax(${stickyColumnStartingWidth}px, 1fr) ${gridColumnWidths}`
      } else {
        // UUID Checkboxes only
        gridColumnWidths = `45px ${gridColumnWidths}`
      }
    } else {
      if (tablePayload.stickyColumn) {
        // Sticky Column only
        gridColumnWidths = `minmax(${stickyColumnStartingWidth}px, 1fr) ${gridColumnWidths}`
      }
      // Not modifications if neither UUID Checkboxes nor Sticky Column
    }
  }

  const boxShadow = () => {
    if (isLeftScrolled && isRightScrolled) {
      return {
        boxShadow: '6px 2px 5px -7px #d4d4d4, -6px 2px 5px -7px #d4d4d4'
      }
    } else if (isLeftScrolled && !isRightScrolled) {
      return {
        boxShadow: '-6px 2px 5px -7px #d4d4d4',
        borderRight: '1px solid #d4d4d4'
      }
    } else if (!isLeftScrolled && isRightScrolled) {
      return {
        boxShadow: '6px 2px 5px -7px #d4d4d4',
        borderLeft: '1px solid #d4d4d4'
      }
    }
  }

  return (
    <div className={`table-wrapper position-relative ${className}`}>

      {loading && (
        <div
          className='w-100 h-100 position-absolute d-flex justify-content-center align-items-center'
          style={{
            zIndex: '1000'
            // backgroundColor: 'rgba(130, 130, 130, 0.10)'
          }}
        >
          <Loading />
        </div>
      )}

      {/* TABLE TOP CONTROLS */}
      <TopControls
        topControls={topControls}
        disabled={loading}
        tablePayload={tablePayload}
        filterGroupsConfig={filterGroupsConfig}
      />

      {/* TABLE CONTENT */}
      {error
        ? <div className='my-5 text-center'>An error has occurred, please try again.</div>
        : (
          <div
            className='table'
            ref={tableElement}
            onScroll={handleScroll}
            style={{
              gridTemplateColumns: gridColumnWidths,
              ...boxShadow()
            }}
          >
            {isHeaderVisible &&
              <TableHeaders
                tablePayload={tablePayload}
                tableElement={tableElement}
                activeColumns={activeColumns}
                UUIDCheckboxes={UUIDCheckboxes}
              />
            }

            <TableRows
              tablePayload={tablePayload}
              loading={loading}
              activeColumns={activeColumns}
              starColumn={starColumn}
              isHeaderVisible={isHeaderVisible}
              UUIDCheckboxes={UUIDCheckboxes}
              styleCallback={styleCallback}
            />
          </div>
        )}

      {/* TABLE BOTTOM CONTROLS */}
      <div className='d-flex flex-column flex-md-row justify-content-between align-items-center mt-4'>
        <div className='fw-semibold text-muted text-nowrap'>
          {`${tablePayload.assembledDataLength} item${tablePayload.assembledDataLength === 1 ? '' : 's'}`}
        </div>
        {tablePayload.totalPages > 1 && (
          <Pagination
            totalPages={tablePayload.totalPages}
            prevDisabled={tablePayload.prevDisabled || loading}
            nextDisabled={tablePayload.nextDisabled || loading}
            setPageNumber={tablePayload.setCurrentPage}
            pageNumber={tablePayload.currentPage}
          />
        )}
        <RowsPerPage
          rowsPerPage={tablePayload.rowsPerPage}
          setRowsPerPage={tablePayload.setRowsPerPage}
          gap={[5, 10, 25, 50]}
          disabled={loading}
        />
      </div>

    </div>
  )
}

Table.propTypes = {
  topControls: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element
  ]),
  tablePayload: PropTypes.object,
  className: PropTypes.string,
  filterGroupsConfig: PropTypes.object,
  styleCallback: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  starColumn: PropTypes.bool,
  isHeaderVisible: PropTypes.bool,
  UUIDCheckboxes: PropTypes.bool
}

export default Table
